// Lib
import React, { Ref, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

// Components
import Caption from '../../components/caption/Caption';
import LinkMetadata from './LinkMetadata';
import LinkHeader from './LinkHeader';
import ResizableEditorOverflowOverlay from '../resizing/resizableEditor/ResizableEditorOverflowOverlay';
import LinkEditor, { LinkEditorProps } from './LinkEditor';

// Hooks
import useEditorOverflowObserver, {
    EditorOverflowObserverProps,
} from '../resizing/resizableEditor/useEditorOverflowObserver';
import useBecameTrueEffect from '../../utils/react/useBecameTrueEffect';
import useStalledBoolean from '../../utils/react/useStalledBoolean';

// Helpers
import { resizableEditorContainerStyle } from '../resizing/resizableEditor/resizableEditorUtils';
import { setShowCaption } from '../../components/caption/captionActions';

// Types & Constants
import { CaptionProps } from '../../components/caption/captionComponentTypes';
import { CAPTION_EDITOR_KEY } from '../../components/caption/captionConstants';
import { LINK_TITLE_EDITOR_KEY } from './LinkConstants';
import { DispatchUpdateElement } from '../actions/elementActionTypes';

import './ResizableLinkMetaSection.scss';

const TARGET_EDITOR_KEYS = [CAPTION_EDITOR_KEY, LINK_TITLE_EDITOR_KEY];

type Props = CaptionProps &
    EditorOverflowObserverProps &
    LinkEditorProps & {
        captionContent: string;
        captionVisible: boolean;
        elementHeight: number;
        editorOverflowed: boolean;
        tempContainerHeight?: number;
        dispatchUpdateElement: DispatchUpdateElement;
        elementId: string;
        isResizing: boolean;
        editorRef?: Ref<HTMLDivElement>;
    };

const LinkMetaContent = (props: Props) => {
    return (
        <div className="LinkMetaContent" ref={props.editorRef}>
            <LinkEditor {...props} />
            <LinkHeader {...props} />
            <LinkMetadata {...props} />
            <Caption {...props} />
        </div>
    );
};

const ResizableLinkMetaSection = (props: Props) => {
    const { elementId, isResizing, captionVisible } = props;
    const dispatch = useDispatch();

    // Force caption to be shown when resizing links without visible media, as the caption is what takes up the vertical space
    const forceShowCaption = isResizing && !captionVisible;
    useBecameTrueEffect(() => {
        dispatch(setShowCaption({ elementId }));
    }, forceShowCaption);

    // Force overflow to prevent flicker when showing caption during resizing, as there is
    // a small delay before editorOverflowed updates, and we know in this case it will always be overflowed
    const forceEditorOverflow = useStalledBoolean(forceShowCaption, 100);

    const { editorOverflowed, editorRef, containerRef, isEditingTargetContent, recalculateOverflow } =
        useEditorOverflowObserver(props, TARGET_EDITOR_KEYS);

    // We always want the caption to take up any extra space, so if the caption is
    // not visible and the height is greater than content, display content height
    const heightStyle = resizableEditorContainerStyle(props, isEditingTargetContent);
    const style = heightStyle?.minHeight && !captionVisible ? {} : heightStyle;

    // trigger a measure when toggling caption
    useEffect(() => {
        if (isResizing) return;

        recalculateOverflow();
    }, [captionVisible]);

    return (
        <div
            className={classNames('ResizableLinkMetaSection', { 'force-overflow': forceEditorOverflow })}
            ref={containerRef}
            style={style}
        >
            <LinkMetaContent {...props} editorRef={editorRef} />
            <ResizableEditorOverflowOverlay show={editorOverflowed || forceEditorOverflow} />
        </div>
    );
};

const LinkMetaSection = ({ isMediaVisible, ...props }: Props & { isMediaVisible: boolean }) =>
    isMediaVisible ? <LinkMetaContent {...props} /> : <ResizableLinkMetaSection {...props} />;

export default LinkMetaSection;
